import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../styles/components/_empreedimentosdetalhes.scss";
import {
  ChevronLeft,
  Edit2,
  EyeIcon,
  Upload,
  Trash2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Home,
  Building2
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import Tooltip from "../../components/tootip/Tootip";
import "react-toastify/dist/ReactToastify.css";
import image1 from "../../assets/images/casa1.jpg";
import image2 from "../../assets/images/casa2.jpg";
import image3 from "../../assets/images/casa3.jpg";
import EnterpriseProvider from "../../infra/provider/enterpriseProvider";
import Enterprise from "../../domain/entities/enterprise";
import EnterpriseStatus from "../../domain/entities/valueObjects/enterpriseStatus";
import ReleaseTypeProvider from "../../infra/provider/releaseTypeProvider";
import ProjectPlanProvider from "../../infra/provider/projectPlanProvider";
import ProjectPlan from "../../domain/entities/projectPlan";
import UnitProvider from "../../infra/provider/unitProvider";
import Unit from "../../domain/entities/unit";
import { Formatter } from "../../utils/formatter";
import UploadImageProvider from "../../infra/provider/uploadImageProvider";
import UnitStatus from "../../domain/entities/valueObjects/unitStatus";
import Room from "../../domain/entities/valueObjects/room";
import PropertyCategoryProvider from "../../infra/provider/propertyCategoryProvider";
import ConstructionCompanyProvider from "../../infra/provider/constructionCompanyProvider";
import ZipCode from "../../domain/entities/valueObjects/zipCode";
import PriceHistory from "../../domain/entities/valueObjects/priceHistory";
import Image from "../../domain/entities/valueObjects/image";


const steps = [
  { id: 0, title: "Visão geral", key: "overview" },
  { id: 1, title: "Características", key: "characteristics" },
  { id: 2, title: "Plantas e unidades", key: "plants-units" },
  { id: 3, title: "Imagens", key: "images" }
];

export function EnterprisesDetails() {
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{
    title: string;
    tags: string[];
  }>({ title: "", tags: [] });
  const isFetching = useRef(false);
  const [enterprise, setEnterprise] = useState<Enterprise>(new Enterprise());
  const [updatedEnterprise, setUpdatedEnterprise] = useState<Enterprise>(new Enterprise());
  const [activeStep, setActiveStep] = useState(0);
  const [unitForm, setUnitForm] = useState({
    id: "",
    quantity: 0,
    price: 0,
    description: "",
    status: new UnitStatus("available"),
    projectPlanId: "",
    priceHistory: [] as PriceHistory[],
    block: "",
    tipoUnidade: "",
    complemento: "",
    numeroOuLote: "",
    endereco: {
      rua: "",
      numero: "",
      bairro: "",
      cidade: ""
    }
  });
  const [projectPlanImage, setProjectPlanImage] = useState<{
    file: File | null;
    progress: number;
    url?: string;
  }>({ file: null, progress: 0 });
  const [newTag, setNewTag] = useState("");
  const enterpriseProvider = EnterpriseProvider.getInstance();
  const releaseTypeProvider = ReleaseTypeProvider.getInstance();
  const projectPlanProvider = ProjectPlanProvider.getInstance();
  const unitProvider = UnitProvider.getInstance();
  const uploadImageProvider = UploadImageProvider.getInstance();
  const propertyCategoryProvider = PropertyCategoryProvider.getInstance();
  const constructionCompanyProvider = ConstructionCompanyProvider.getInstance();
  const { id } = useParams();
  const [isEditModalInfoOpen, setIsEditModalInfoOpen] = useState(false);
  const [unitPriceHistory, setUnitPriceHistory] = useState<PriceHistory[]>([]);

  useEffect(() => {
    getEnterpriseDetails();
  }, []);

  useEffect(() => {
    if (isEditModalInfoOpen) {
      setUpdatedEnterprise({
        ...enterprise,
        category: enterprise.category,
        releaseType: enterprise.releaseType,
        constructionCompany: enterprise.constructionCompany
      });
    }
  }, [isEditModalInfoOpen, enterprise]);

  const getEnterpriseDetails = async () => {
    if (isFetching.current) return;
    try {
      isFetching.current = true;
      const response = await enterpriseProvider.getEnterpriseById(id as string);
      setEnterprise(response);
    } catch (error) {
      console.error(error);
    }
    isFetching.current = false;
  };

  const toggleDescription = () => setDescriptionOpen(!descriptionOpen);
  const showTagsModal = (category: string) => {
    const tagsByCategory = {
      Tags: enterprise.tags,
      Serviços: enterprise.services,
      "Areas Comuns": enterprise.commonAreas,
    };
    setModalContent({
      title: category,
      tags: tagsByCategory[category as keyof typeof tagsByCategory],
    });
    setModalOpen(true);
  };

  const openModal = (title: string, tags: string[]) => {
    setModalContent({ title, tags });
    setModalOpen(true);
  };

  const handleAddTag = () => {
    if (newTag.trim() === "") return;
    const updatedTags = [...modalContent.tags, newTag];
    updateTags(modalContent.title, updatedTags);
    setModalContent({ ...modalContent, tags: updatedTags });
    setNewTag("");
  };

  const handleRemoveTag = (index: number) => {
    const updatedTags = modalContent.tags.filter((_, i) => i !== index);
    updateTags(modalContent.title, updatedTags);
    setModalContent({ ...modalContent, tags: updatedTags });
  };

  const updateTags = (category: string, updatedTags: string[]) => {
    const updatedEnterprise = { ...enterprise };
    if (category === "Tags") updatedEnterprise.tags = updatedTags;
    if (category === "Serviços") updatedEnterprise.services = updatedTags;
    if (category === "Areas Comuns") updatedEnterprise.commonAreas = updatedTags;
    setEnterprise(updatedEnterprise);
  };

  const [isPlantModalOpen, setIsPlantModalOpen] = useState(false);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [isUnitModalIncreasedOpen, setIsUnitModalIncreasedOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleCreatePlant = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Garantir que todos os valores de quantity sejam números válidos
      const plantToCreate = {
        ...projectPlanForm,
        rooms: projectPlanForm.rooms.map(room => ({
          ...room,
          quantity: room.quantity || 0
        }))
      };

      // Adicionar a imagem se foi feito upload (mesmo padrão do botão Nova Imagem)
      if (projectPlanImage.url) {
        plantToCreate.images = [{ id: 0, imageLink: projectPlanImage.url }];
      }

      await projectPlanProvider.createEnterpriseProjectPlan(
        enterprise.id,
        plantToCreate
      );
      toast.success("Planta criada com sucesso!");
      setIsPlantModalOpen(false);
      setProjectPlanForm(new ProjectPlan());
      setProjectPlanImage({ file: null, progress: 0 });
      getEnterpriseDetails();
    } catch (error) {
      console.error("Erro ao criar planta:", error);
      toast.error("Erro ao criar planta");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePlantModal = () => {
    setIsPlantModalOpen(false);
    setProjectPlanForm(new ProjectPlan());
    toast.info("Não se preocupe, nenhuma informação foi salva!");
  };

  const handleOpenUnitModal = () => {
    // Limpa o formulário antes de abrir o modal
    setUnitForm({
      id: "",
      quantity: 0,
      price: 0,
      description: "",
      status: new UnitStatus("available"),
      projectPlanId: "",
      priceHistory: [],
      block: "",
      tipoUnidade: "",
      complemento: "",
      numeroOuLote: "",
      endereco: {
        rua: "",
        numero: "",
        bairro: "",
        cidade: ""
      }
    });
    setIsUnitModalOpen(true);
  };

  const handleCloseUnitModal = () => {
    setIsUnitModalOpen(false);
    setUnitForm({
      id: "",
      quantity: 0,
      price: 0,
      description: "",
      status: new UnitStatus("available"),
      projectPlanId: "",
      priceHistory: [],
      block: "",
      tipoUnidade: "",
      complemento: "",
      numeroOuLote: "",
      endereco: {
        rua: "",
        numero: "",
        bairro: "",
        cidade: ""
      }
    });
    toast.info("Não se preocupe, nenhuma informação foi salva!");
  };

  const handleCloseUnitIncreasedModal = () => {
    cleanPercentage();
    setIsUnitModalIncreasedOpen(false);
    setUnitForm({
      id: "",
      quantity: 0,
      price: 0,
      description: "",
      status: new UnitStatus("available"),
      projectPlanId: "",
      priceHistory: [],
      block: "",
      tipoUnidade: "",
      complemento: "",
      numeroOuLote: "",
      endereco: {
        rua: "",
        numero: "",
        bairro: "",
        cidade: ""
      }
    });
    toast.info("Não se preocupe, nenhuma informação foi salva!");
  };

  const navigate = useNavigate();
  const handleBackClick = () => {
    navigate("/empreendimentos/visualizar");
  };

  const handleCreateUnit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validação dos campos obrigatórios
      if (!unitForm.tipoUnidade) {
        toast.error("Selecione o tipo de unidade");
        return;
      }

      if (unitForm.tipoUnidade === "apartment" && !unitForm.complemento) {
        toast.error("Informe os detalhes da unidade (Bloco, Quadra ou Torre)");
        return;
      }

      if (unitForm.tipoUnidade === "house") {
        if (!unitForm.endereco.rua || !unitForm.endereco.numero) {
          toast.error("Preencha todos os campos do endereço");
          return;
        }
      }

      // Prepara os dados para envio ao backend
      const formattedUnit = {
        ...unitForm,
        price: unitForm.price,
        block: unitForm.tipoUnidade === "apartment" ? unitForm.complemento : unitForm.numeroOuLote,
        unitType: translateUnitTypeToEnglish(unitForm.tipoUnidade),
        complemento: unitForm.complemento,
        numeroOuLote: unitForm.tipoUnidade === "house" ? unitForm.numeroOuLote : "",
        endereco: unitForm.tipoUnidade === "house" ? unitForm.endereco : {
          rua: "",
          numero: "",
          bairro: "",
          cidade: ""
        },
        status: unitForm.status
      };

      await unitProvider.createEnterpriseUnit(enterprise.id, formattedUnit as Unit);
      toast.success("Unidade criada com sucesso!");
      setIsUnitModalOpen(false);
      getEnterpriseDetails();
    } catch (error) {
      toast.error("Erro ao criar unidade");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEnterprise = async (
    e: any
  ) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await enterpriseProvider.updateEnterprise(updatedEnterprise);
      toast.success("Informações editadas com sucesso!");
    } catch (error) {
      toast.error("Erro ao alterar as informações do empreendimento");
    }
    finally {
      setIsEditModalInfoOpen(false);
      getEnterpriseDetails();
      setIsLoading(false);
    }
  };

  const handleUpdateEnterpriseStatus = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.preventDefault();
    const status = new EnterpriseStatus(e.target.value);
    try {
      await enterpriseProvider.updateEnterpriseStatus(enterprise.id, status);
      setEnterprise((prevEnterprise) => ({
        ...prevEnterprise,
        status: status,
      }));
      toast.success("Status alterado com sucesso!");
    } catch (error) {
      toast.error("Erro ao alterar status do empreendimento");
    }
  };

  const handleUpdateEnterpriseReleaseType = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.preventDefault();
    const releaseTypeId = e.target.value;
    try {
      enterpriseProvider.updateEnterpriseReleaseType(
        enterprise.id,
        releaseTypeId
      );
      const releaseType = releaseTypeProvider.getReleaseTypeById(releaseTypeId);
      setEnterprise((prevEnterprise) => ({
        ...prevEnterprise,
        releaseType: releaseType!,
      }));
      toast.success("Estágio alterado com sucesso!");
    } catch (error) {
      toast.error("Erro ao alterar estágio do empreendimento");
    }
  };

  const handleProjectPlanFileChange = async (files: FileList) => {
    const file = files[0];
    if (!file) return;

    if (file.size / 1024 / 1024 > 10) {
      toast.error("O arquivo excede o tamanho máximo de 10 MB");
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error("Formato de arquivo não suportado. Use apenas PNG ou JPEG");
      return;
    }

    try {
      toast.info("Fazendo upload da imagem...");
      setProjectPlanImage({ file, progress: 0 });

      // Upload da imagem imediatamente (mesmo padrão do botão Nova Imagem)
      const imageUrl = await uploadImageProvider.upload(file);
      if (!imageUrl) {
        toast.error("Erro ao fazer upload da imagem");
        setProjectPlanImage({ file: null, progress: 0 });
        return;
      }

      // Armazenar a URL da imagem
      setProjectPlanImage({ file, progress: 100, url: imageUrl });
      toast.success("Imagem carregada com sucesso!");
    } catch (error) {
      console.error("Erro ao fazer upload da imagem:", error);
      toast.error("Erro ao fazer upload da imagem");
      setProjectPlanImage({ file: null, progress: 0 });
    }
  };

  const [isEditUnitModalOpen, setIsEditUnitModalOpen] = useState(false);

  const handleEditUnit = (unit: any) => {
    const updatedUnit = {
      ...unit,
      status: new UnitStatus(unit.status.getStatus ? unit.status.getStatus() : unit.status),
      price: Formatter.clearSymbolsAndLetters(unit.price),
      block: unit.block || "",
      tipoUnidade: unit.tipoUnidade || "",
      tipoDaUnidade: unit.tipoDaUnidade || "",
      numeroOuLote: unit.numeroOuLote || "",
      endereco: unit.endereco || {
        rua: "",
        numero: "",
        bairro: "",
        cidade: ""
      }
    };
    setSelectedUnit(updatedUnit);
    setUnitForm(updatedUnit);
    setIsEditUnitModalOpen(true);
  };

  const handleDeleteUnit = (unit: any) => {
    setSelectedUnit(unit.id);
    setIsDeleteModalUnitOpen(true);
  };

  const handleCloseEditUnitModal = () => {
    setIsEditUnitModalOpen(false);
    setSelectedUnit(null);
  };

  const confirmDeleteUnits = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await unitProvider.deleteUnit(selectedUnit!);
      toast.success("Unidade excluída com sucesso!");
    } catch (error) {
      toast.error("Não foi possivel excluir a unidade pois ela já possui uma proposta");
    } finally {
      setIsDeleteModalUnitOpen(false);
      setSelectedUnit(null);
      getEnterpriseDetails();
      setIsLoading(false);
    }
    setIsEditUnitModalOpen(false);
  };

  const handleSaveUnitChanges = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validação dos campos obrigatórios
      if (!unitForm.tipoUnidade) {
        toast.error("Selecione o tipo de unidade");
        return;
      }

      if (unitForm.tipoUnidade === "apartment" && !unitForm.complemento) {
        toast.error("Informe os detalhes da unidade (Bloco, Quadra ou Torre)");
        return;
      }

      if (unitForm.tipoUnidade === "house") {
        if (!unitForm.endereco.rua || !unitForm.endereco.numero) {
          toast.error("Preencha todos os campos do endereço");
          return;
        }
      }

      // Prepara os dados para envio ao backend
      const formattedUnit = new Unit(
        unitForm.id,
        unitForm.quantity,
        unitForm.price,
        unitForm.description,
        unitForm.status,
        unitForm.projectPlanId,
        unitForm.priceHistory,
        unitForm.tipoUnidade === "apartment" ? unitForm.complemento : unitForm.numeroOuLote,
        translateUnitTypeToEnglish(unitForm.tipoUnidade),
        unitForm.complemento,
        unitForm.tipoUnidade === "house" ? unitForm.numeroOuLote : "",
        unitForm.tipoUnidade === "house" ? unitForm.endereco : {
          rua: "",
          numero: "",
          bairro: "",
          cidade: ""
        }
      );

      await unitProvider.updateUnit(formattedUnit);
      toast.success("Unidade atualizada com sucesso!");
      getEnterpriseDetails();
    } catch (error) {
      toast.error("Erro ao atualizar a unidade");
    } finally {
      setIsLoading(false);
      setIsEditUnitModalOpen(false);
      setSelectedUnit(null);
    }
  };

  const handleGetUnitById = async (unit: string) => {
    setIsLoading(true);
    setIsDetailModalOpen(true);
    try {
      const response = await unitProvider.getUnitById(unit);
      setUnitPriceHistory(response.priceHistory);
    } catch (error) {
      toast.error("Erro ao buscar dados da unidade");
    } finally {
      setIsLoading(false);
    }
  };


  const handleDeleteProjectPlanImage = () => {
    setSelectedPlan((prevSelectedPlan) => {
      if (!prevSelectedPlan) return prevSelectedPlan;
      return {
        ...prevSelectedPlan,
        images: []
      };
    });
  };

  const handleDropProjectPlanImage = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      handleProjectPlanFileChange(e.dataTransfer.files);
    }
  };

  const handleAddPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validação de tamanho e tipo
    if (file.size / 1024 / 1024 > 10) {
      toast.error("O arquivo excede o tamanho máximo de 10 MB");
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error("Formato de arquivo não suportado. Use apenas PNG ou JPEG");
      return;
    }

    try {
      toast.info("Fazendo upload da imagem...");
      // Upload da imagem
      const imageUrl = await uploadImageProvider.upload(file);
      if (!imageUrl) {
        toast.error("Erro ao fazer upload da imagem");
        return;
      }
      // Envia para a API do empreendimento
      const response: any = await enterpriseProvider.addEnterpriseImage(enterprise.id, imageUrl, false);
      // Atualiza o estado local com o novo array de imagens
      if (response && response.images && Array.isArray(response.images)) {
        setEnterprise((prev: any) => ({
          ...prev,
          images: response.images.map((img: any) => Image.fromJson(img)),
        }));
        toast.success("Foto adicionada com sucesso!");
      } else {
        // fallback: buscar o empreendimento atualizado
        await getEnterpriseDetails();
        toast.success("Foto adicionada com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao fazer upload ou salvar imagem");
    }
  };

  const handleFileInputClick = () => {
    const fileInput = document.getElementById("add-photo") as HTMLInputElement;
    fileInput?.click();
  };

  const handleDeletePhoto = async (index: number) => {
    debugger;
    const image = enterprise.images[index];
    if (!image || !image.id) {
      toast.error('Imagem não pode ser excluída (sem ID válido).');
      return;
    }
    try {
      await enterpriseProvider.deleteEnterpriseImage(enterprise.id, image.id);
      // Atualiza o estado local removendo a imagem
      setEnterprise((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      }));
      // Ajusta o índice da foto atual, se necessário
      setCurrentPhotoIndex((prev) => Math.max(0, prev - (index === prev ? 1 : 0)));
      toast.success("Imagem excluída com sucesso!");
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('Imagem não encontrada no servidor.');
      } else {
        toast.error('Erro ao excluir imagem. Tente novamente ou contate o suporte.');
      }
    }
  };

  const handleDeleteProjectPlanImageInPut = async (imageId: number) => {
    try {
      if (selectedPlan?.id) {
        await projectPlanProvider.deleteProjectPlanImage(selectedPlan.id.toString(), imageId.toString());
        setSelectedPlan((prev: any) => ({
          ...prev,
          images: [],
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditPhoto = (index: number) => {
    const inputElement = document.getElementById(`edit-photo-${index}`) as HTMLInputElement;
    inputElement?.click();
  };

  const handleEditFile = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    if (file) {
      const updatedPhotoUrl = URL.createObjectURL(file);
      setEnterprise((prev: any) => {
        const updatedImages = [...prev.images];
        updatedImages[index].path = updatedPhotoUrl;
        return { ...prev, images: updatedImages };
      });
      toast.success("Foto atualizada com sucesso!");
    }
  };

  const scrollContainer = document.querySelector('.small-photos-scroll');
  if (scrollContainer && enterprise.images.length > 5) {
    scrollContainer.setAttribute('data-remaining', String(enterprise.images.length - 4));
  }

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectPlanForm, setProjectPlanForm] = useState(new ProjectPlan());
  const [selectedPlan, setSelectedPlan] = useState<ProjectPlan | undefined>(undefined);
  const [isDeleteModalUnitOpen, setIsDeleteModalUnitOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [percentage, setPercentage] = useState<string>("");
  const [percentageType, setPercentageType] = useState("");

  const handleDeletePlants = (id: any) => {
    setSelectedPlan(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDeletePlants = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await projectPlanProvider.deleteProjectPlan(`${selectedPlan}`);
      toast.success("Planta excluída com sucesso!");
    } catch (error) {
      toast.error("Não foi possivel excluir a planta pois ela já está vinculada a uma unidade.");
    } finally {
      setSelectedUnit(null);
      getEnterpriseDetails();
      setIsLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleCancelDeletePlants = () => {
    setIsDeleteModalOpen(false);
    //setSelectedPlan(null);
    toast.info("Não se preocupe, nenhuma planta foi deletada!");
  };

  const handleCancelDeleteUnits = () => {
    setIsDeleteModalUnitOpen(false);
    setSelectedUnit(null);
    toast.info("Não se preocupe, nenhuma planta foi deletada!");
  };

  const [units, setUnits] = useState<Unit[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    if (enterprise.units) {
      setUnits(enterprise.units);
    }
  }, [enterprise.units]);

  const handleStatusChange = (unitId: string, newStatus: string) => {
    setUnits((prevUnits) => {
      const updatedUnits = prevUnits.map((unit) =>
        unit.id === unitId
          ? { ...unit, status: new UnitStatus(newStatus) }
          : unit
      );
      return updatedUnits;
    });
    handleUpdateUnit(unitId, newStatus);
  };

  const handleUpdateUnit = async (unitId: string, newStatus: string) => {
    setIsLoading(true);
    try {
      await unitProvider.updateUnitStatus(unitId, newStatus);
      toast.success("Status da unidade atualizado com sucesso!");
    } catch (error) {
      toast.error("Erro ao atualizar status da unidade");
    } finally {
      getEnterpriseDetails();
      setIsLoading(false);
    }
  };

  const [unitStatus] = useState(new UnitStatus("available"));

  const handleEditClick = () => {
    setIsEditModalInfoOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalInfoOpen(false);
  };

  const handleInputInfoChange = (e: any) => {
    const { name, value } = e.target;
    setUpdatedEnterprise((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    toast.success("Informações editadas com sucesso!");
    handleCloseEditModal();
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const handleEditPlant = (plan: any) => {
    // Garantir que todos os valores de quantity sejam números válidos
    const planWithValidQuantities = {
      ...plan,
      rooms: plan.rooms.map((room: any) => ({
        ...room,
        quantity: room.quantity || 0
      }))
    };

    setSelectedPlan(planWithValidQuantities);
    setIsEditModalOpen(true);
  };

  const handleSavePlantChanges = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Garantir que todos os valores de quantity sejam números válidos
      const updatedPlan = {
        ...selectedPlan!,
        rooms: selectedPlan!.rooms.map(room => ({
          ...room,
          quantity: room.quantity || 0
        }))
      };

      await projectPlanProvider.updateProjectPlan(updatedPlan);
      toast.success("Planta editada com sucesso!");
    } catch (error) {
      console.error("Erro ao editar planta:", error);
      toast.error("Erro ao editar a planta.");
    } finally {
      getEnterpriseDetails();
      setIsLoading(false);
      setIsEditModalOpen(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: keyof Room
  ) => {
    const value = Number(e.target.value) || 0;

    setProjectPlanForm((prevState) => ({
      ...prevState,
      rooms: prevState.rooms.map((room, i) =>
        i === index ? { ...room, [field]: value } : room
      ),
    }));
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: keyof Room
  ) => {
    const value = Number(e.target.value) || 0;

    // Atualiza o estado de selectedPlan
    setSelectedPlan((prevState) => prevState && {
      ...prevState,
      rooms: prevState.rooms.map((room, i) =>
        i === index ? { ...room, [field]: value } : room
      ),
    });
  };

  const cleanPercentage = () => {
    setPercentage('');
    setPercentageType('');
  }

  const handleUpdatePercentageModal = async () => {
    if (percentageType === '')
      return toast.error("Informe a ação que deseja.");

    if (percentage === null || percentage === '')
      return toast.error("Informe uma porcentagem.");

    if (Number(percentage) === 0)
      return toast.error("Informe uma porcentagem maior que zero.");

    try {
      const percentageNumber = Number(percentage);

      // O backend já recebe os valores em centavos, então não precisa fazer conversão adicional
      await enterpriseProvider.updateEnterpriseUnitPercentage(enterprise.id, percentageNumber, percentageType);
      toast.success("Alteração em massa realizada com sucesso!");
      await getEnterpriseDetails();
    } catch (error) {
      toast.error("Não foi possivel realizar a Alteração em massa.");
    } finally {
      setIsUnitModalIncreasedOpen(false);
      cleanPercentage();
    }
  };

  const formatUnitData = (unit: any) => {
    return {
      ...unit,
      price: Formatter.formatNumberToBRCurrency(unit.price),
      tipoUnidade: unit.tipoUnidade || "",
      status: unit.status instanceof UnitStatus ? unit.status : new UnitStatus(unit.status)
    };
  };

  // Função para traduzir o tipo de unidade para inglês
  const translateUnitTypeToEnglish = (tipoUnidade: string): string => {
    switch (tipoUnidade) {
      case "Apartamento":
        return "apartment";
      case "Casa":
        return "house";
      default:
        return tipoUnidade; // Retorna o original caso não encontre tradução
    }
  };

  useEffect(() => {
    if (enterprise.units) {
      setUnits(enterprise.units);
    }
  }, [enterprise.units]);

  // Função para adicionar novas imagens ao plano selecionado
  const handleAddImagesToPlan = async (files: FileList) => {
    if (!selectedPlan) return;

    try {
      toast.info("Fazendo upload da imagem...");

      // Processar cada arquivo
      const newImages = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validação de tamanho e tipo
        if (file.size / 1024 / 1024 > 10) {
          toast.error(`O arquivo ${file.name} excede o tamanho máximo de 10 MB`);
          continue;
        }
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
          toast.error(`Formato de arquivo não suportado para ${file.name}. Use apenas PNG ou JPEG`);
          continue;
        }

        // Upload da imagem (mesmo padrão do botão Nova Imagem)
        const imageUrl = await uploadImageProvider.upload(file);
        if (imageUrl) {
          newImages.push({
            id: 0,
            imageLink: imageUrl, // URL real do servidor
            file: file // Manter referência ao arquivo para preview local
          });
        }
      }

      if (newImages.length > 0) {
        setSelectedPlan({
          ...selectedPlan,
          images: [...selectedPlan.images, ...newImages] as any
        });
        toast.success(`${newImages.length} imagem(ns) carregada(s) com sucesso!`);
      }
    } catch (error) {
      console.error("Erro ao fazer upload da imagem:", error);
      toast.error("Erro ao fazer upload da imagem");
    }
  };

  // Função para remover imagem do plano selecionado
  const handleRemoveImageFromPlan = (imageId: number | string) => {
    if (!selectedPlan) return;

    setSelectedPlan({
      ...selectedPlan,
      images: selectedPlan.images.filter((img: any) => img.id !== imageId)
    });
  };

  // Função para renderizar o conteúdo da seção Visão geral
  const renderOverviewContent = () => (
    <div className="general-info">
      <div className="opsops">
        <h2>Informações gerais:</h2>
        <button className="edit-button" onClick={handleEditClick}>
          <Edit2 size={18} />
          <span>Editar informações gerais</span>
        </button>
      </div>
      <div className="info-box">
        <div className="edit-section-info"></div>
        <div className="info-row">
          <div className="info-item">
            <label>Nome:</label>
            <span>{enterprise.name}</span>
          </div>
          <div className="info-item">
            <label>Tipo de lançamento:</label>
            <span>{enterprise.releaseType.name}</span>
          </div>
        </div>

        <div className="divisor-enterprise"></div>
        <div className="info-row">
          <div className="info-item">
            <label>Categoria:</label>
            <span>{enterprise.category.name}</span>
          </div>
          <div className="info-item">
            <label>Construtora:</label>
            <span>{enterprise.constructionCompany.name}</span>
          </div>
        </div>
        <div className="divisor-enterprise"></div>
        <div className="info-row description">
          <div className="description-header" onClick={toggleDescription}>
            <label>Descrição:</label>
            {descriptionOpen ? <ChevronUp /> : <ChevronDown />}
          </div>
          {descriptionOpen && <p>{enterprise.description}</p>}
        </div>
        <div className="divisor-enterprise"></div>
        <div className="info-row">
          <div className="info-item">
            <label>CEP:</label>
            <span>{enterprise.address.zipCode.getCodeFormatted()}</span>
          </div>
          <div className="info-item">
            <label>Cidade:</label>
            <span>{enterprise.address.city}</span>
          </div>
        </div>
        <div className="divisor-enterprise"></div>
        <div className="info-row">
          <div className="info-item">
            <label>Logradouro:</label>
            <span>{enterprise.address.street}</span>
          </div>
          <div className="info-item">
            <label>Número:</label>
            <span>{enterprise.address.number}</span>
          </div>
        </div>
        <div className="divisor-enterprise"></div>
        <div className="info-row">
          <div className="info-item">
            <label>Bairro:</label>
            <span>{enterprise.address.neighborhood}</span>
          </div>
          <div className="info-item">
            <label>Estado:</label>
            <span>{enterprise.address.uf}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Função para renderizar o conteúdo da seção Características
  const renderCharacteristicsContent = () => (
    <div className="characteristics">
      <h2>Características:</h2>
      <div className="characteristics-box">
        <div className="tag-section">
          <div className="tag-header">
            <h3>Tags:</h3>
            <button
              onClick={() => showTagsModal("Tags")}
              className="see-more"
            >
              Ver mais
            </button>
          </div>
          <div className="tags-grid">
            {enterprise.tags.map((tag, i) => (
              <span key={i} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
        <div className="divisor-enterprise"></div>
        <div className="tag-section">
          <div className="tag-header">
            <h3>Serviços:</h3>
            <button
              onClick={() => showTagsModal("Serviços")}
              className="see-more"
            >
              Ver mais
            </button>
          </div>
          <div className="tags-grid">
            {enterprise.services.map((service, i) => (
              <span key={i} className="tag">
                {service}
              </span>
            ))}
          </div>
        </div>
        <div className="divisor-enterprise"></div>
        <div className="tag-section">
          <div className="tag-header">
            <h3>Áreas comuns:</h3>
            <button
              onClick={() => showTagsModal("Areas Comuns")}
              className="see-more"
            >
              Ver mais
            </button>
          </div>
          <div className="tags-grid">
            {enterprise.commonAreas.map((area, i) => (
              <span key={i} className="tag">
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Função para renderizar o conteúdo da seção Plantas e unidades
  const renderPlantsAndUnitsContent = () => (
    <>
      {/* Seção Plantas */}
      <div className="plants">
        <div className="section-header">
          <h2>Plantas:</h2>
          <button
            className="add-button"
            onClick={() => setIsPlantModalOpen(true)}
          >
            + Nova planta
          </button>
        </div>
        <div className="table-box-enterprise">
          <table className="table-box-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Área total (m²)</th>
                <th>Área útil/privativa (m²)</th>
                <th>Quantidade de cômodos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    Carregando...
                  </td>
                </tr>
              ) : enterprise.projectPlans.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    Nenhum item criado.
                  </td>
                </tr>
              ) : (
                enterprise.projectPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.id}</td>
                    <td>{plan.name}</td>
                    <td>{plan.area}m²</td>
                    <td>{plan.builtArea}m²</td>
                    <td>{plan.rooms.reduce((sum, room) => sum + room.quantity, 0)}</td>
                    <td>
                      <Tooltip text="Editar">
                        <button
                          className="action-button"
                          onClick={() => handleEditPlant(plan)}>
                          <Edit2 size={18} />
                        </button>
                      </Tooltip>
                      <Tooltip text="Excluir">
                        <button
                          className="action-button"
                          onClick={() => handleDeletePlants(plan.id)}
                        >
                          <Trash2 size={18} />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seção Unidades */}
      <div className="units">
        <div className="section-header">
          <h2>Unidades:</h2>
          <div className="box-details-button">
            <button className="add-button" onClick={() => setIsUnitModalIncreasedOpen(true)}>Alteração em massa</button>
            <button className="add-button" onClick={handleOpenUnitModal}>+ Nova unidade</button>
          </div>
        </div>
        <div className="table-box-enterprise">
          <table className="table-box-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Descrição</th>
                <th>Planta</th>
                <th>Preço</th>
                <th>Tipo de unidade</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    Carregando...
                  </td>
                </tr>
              ) : enterprise.units.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    Nenhum item criado.
                  </td>
                </tr>
              ) : (
                enterprise.units.map((unit) => {
                  const formattedUnit = formatUnitData(unit);
                  return (
                    <tr key={formattedUnit.id}>
                      <td>{formattedUnit.id}</td>
                      <td>{formattedUnit.description}</td>
                      <td>{formattedUnit.projectPlanId}</td>
                      <td>{formattedUnit.price}</td>
                      <td>
                        <div className="unit-type">
                          {formattedUnit.tipoUnidade === "house" ? (
                            <>
                              <Home size={16} />
                              <span>Casa</span>
                            </>
                          ) : (
                            <>
                              <Building2 size={16} />
                              <span>Apartamento</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <select
                          className={`select-status status-${units.find((u) => u.id === formattedUnit.id)?.status.getStatus()}`}
                          value={units.find((u) => u.id === formattedUnit.id)?.status.getStatus() || ''}
                          onChange={(e) => handleStatusChange(formattedUnit.id, e.target.value)}
                        >
                          {formattedUnit.status.ALL.map((status: string) => (
                            <option
                              key={status}
                              value={status}
                            >
                              {formattedUnit.status.unitStatusMap[status]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <Tooltip text="Editar">
                          <button
                            className="action-button"
                            onClick={() => handleEditUnit(formattedUnit)}>
                            <Edit2 size={18} />
                          </button>
                        </Tooltip>

                        <Tooltip text="Excluir">
                          <button
                            className="action-button"
                            onClick={() => handleDeleteUnit(formattedUnit)}>
                            <Trash2 size={18} />
                          </button>
                        </Tooltip>
                        <Tooltip text="Detalhes">
                          <button
                            className="action-button"
                            onClick={() => {
                              setUnitForm({
                                ...formattedUnit,
                                tipoUnidade: formattedUnit.tipoUnidade || "",
                                tipoDaUnidade: formattedUnit.tipoDaUnidade || "",
                                numeroOuLote: formattedUnit.numeroOuLote || "",
                                endereco: formattedUnit.endereco || {
                                  rua: "",
                                  numero: "",
                                  bairro: "",
                                  cidade: ""
                                }
                              });
                              handleGetUnitById(formattedUnit.id)
                            }}
                          >
                            <EyeIcon size={18} />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  // Função para renderizar o conteúdo da seção Imagens
  const renderImagesContent = () => (
    <div className="photos-section">
      <div className="section-header">
        <h2 className="data-box-details-title-h2">Imagens:</h2>
        <button onClick={handleFileInputClick} className="add-button">
          + Nova imagem
        </button>
        <input
          type="file"
          id="add-photo"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleAddPhoto}
        />
      </div>
      <div className="photo-gallery">
        {/* Botão anterior */}
        <button
          className="gallery-nav prev"
          onClick={() =>
            setCurrentPhotoIndex((prev) =>
              prev > 0 ? prev - 1 : enterprise.images.length - 1
            )
          }
        >
          <ChevronLeft />
        </button>

        <div className="photo-grid">
          {/* Miniaturas das imagens */}
          <div className="small-photos">
            <div className="small-photos-scroll">
              {enterprise.images.map((image, index) => (
                <div key={index} className="thumbnail-container">
                  <img
                    src={image.path}
                    alt={`Miniatura ${index + 1}`}
                    className={`thumbnail ${index === currentPhotoIndex ? 'active' : ''}`}
                    onClick={() => setCurrentPhotoIndex(index)}
                  />
                </div>

              ))}
            </div>
          </div>

          {/* Imagem principal */}
          <div className="main-photo-container">
            <img
              src={enterprise.images[currentPhotoIndex]?.path}
              alt={`Foto ${currentPhotoIndex + 1}`}
              className="main-photo"
            />
            <div className="thumbnail-controls">
              <button
                onClick={() => handleDeletePhoto(currentPhotoIndex)}
                title="Excluir"
              >
                <Trash2 size={14} />
              </button>
              <input
                type="file"
                id={`edit-photo-${currentPhotoIndex}`}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleEditFile(e, currentPhotoIndex)}
              />
            </div>
          </div>
        </div>

        {/* Navegação seguinte */}
        <button
          className="gallery-nav next"
          onClick={() =>
            setCurrentPhotoIndex((prev) => prev < enterprise.images.length - 1 ? prev + 1 : 0
            )}>
          <ChevronRight />
        </button>
      </div>
    </div>
  );

  // Função para renderizar o conteúdo baseado no step ativo
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderOverviewContent();
      case 1:
        return renderCharacteristicsContent();
      case 2:
        return renderPlantsAndUnitsContent();
      case 3:
        return renderImagesContent();
      default:
        return renderOverviewContent();
    }
  };

  return (
    <div className="property-details">
      <div className="header-section">
        <div className="header-title">
          <ChevronLeft
            size={16}
            onClick={handleBackClick}
            className="back-arrow"
            style={{ cursor: "pointer" }}
          />
          <h1 className="property-details-h1">Detalhes do empreendimento:</h1>
        </div>
        <div className="header-filters">
          <select
            className="filter-select"
            value={enterprise.status.getStatus()}
            onChange={handleUpdateEnterpriseStatus}
          >
            <option value="">{enterprise.status.getFormatted()}</option>
            {enterprise.status.ALL.filter(
              (status) => status !== enterprise.status.getStatus()
            ).map((status) => (
              <option key={status} value={status}>
                {enterprise.status.enterpriseStatusMap[status]}
              </option>
            ))}
          </select>
          <select
            className="filter-select"
            value={enterprise.releaseType.id}
            onChange={handleUpdateEnterpriseReleaseType}
          >
            <option value="">{enterprise.releaseType.name}</option>
            {releaseTypeProvider
              .getReleaseTypes()
              .filter((rt) => rt.id !== enterprise.releaseType.id)
              .map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Navegação por abas */}
      <div className="steps-navigation">
        {steps.map((step) => (
          <button
            key={step.id}
            className={`step-tab ${activeStep === step.id ? 'active' : ''}`}
            onClick={() => setActiveStep(step.id)}
          >
            {step.title}
          </button>
        ))}
      </div>

      {/* Conteúdo dinâmico baseado na aba ativa */}
      <div className="step-content">
        {renderStepContent()}
      </div>

      {/* Modal de detalhes - unidades */}
      {isDetailModalOpen && (
        <div className="modal-overlay-details-unit">
          <div className="modal-container-details-unit">
            <button className="close-button-details-unit" onClick={() => setIsDetailModalOpen(false)}>×</button>
            <h2>Detalhes das unidades:</h2>
            <p className="description-details-unit">
              Acompanhe abaixo as mudanças de valores relacionado a essa unidade.
            </p>
            <h3>Histórico:</h3>
            <div className="divisor-enterprise"></div>
            <div className="content-details-unit">
              {unitPriceHistory.length > 0 && (
                <>
                  <div className="value-section-details-unit">
                    <p>Valor atual ({new Date(unitPriceHistory[unitPriceHistory.length - 1].createdAt * 1000).toLocaleDateString()}):</p>
                    <div className="value-row-details-unit">
                      <span className="price-du">{Formatter.formatNumberToBRCurrency(Number(unitPriceHistory[unitPriceHistory.length - 1].price * 100))}</span>
                      {unitPriceHistory.length > 1 && (
                        <div className={`details-unit-tag-${unitPriceHistory[unitPriceHistory.length - 1].price > unitPriceHistory[unitPriceHistory.length - 2].price ? 'green' : 'red'}`}>
                          <span className={`${unitPriceHistory[unitPriceHistory.length - 1].price > unitPriceHistory[unitPriceHistory.length - 2].price ? 'increase-du' : 'decrease-du'}`}>
                            {(((unitPriceHistory[unitPriceHistory.length - 1].price - unitPriceHistory[unitPriceHistory.length - 2].price) / unitPriceHistory[unitPriceHistory.length - 2].price) * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="divisor-enterprise"></div>
                  {unitPriceHistory.slice(0, -1).reverse().map((history, index) => (
                    <div key={index}>
                      <div className="value-section-details-unit">
                        <p>Valor anterior ({new Date(history.createdAt * 1000).toLocaleDateString()}):</p>
                        <div className="value-row-details-unit">
                          <span className="price-du">{Formatter.formatNumberToBRCurrency(Number(history.price * 100))}</span>
                        </div>
                      </div>
                      {index < unitPriceHistory.length - 2 && <div className="divisor-enterprise"></div>}
                    </div>
                  ))}
                </>
              )}
            </div>

            <button className="create-button-unidade-eye" onClick={() => setIsDetailModalOpen(false)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão - plantas */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Tem certeza que deseja excluir esta planta?</h3>
            <p>Ao confirmar, essa ação não poderá ser revertida.</p>
            <div className="modal-actions">
              <button
                className="cancel-button-unidade"
                onClick={handleCancelDeletePlants}
              >
                Cancelar
              </button>
              <button
                className="confirm-button"
                onClick={confirmDeletePlants}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de ação em massa - unidades */}
      {isUnitModalIncreasedOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Aumento/redução em massa</h2>
            </div>
            <p>Informe a porcentagem de aumento/redução para aplicar a todos os itens.</p>
            <div className="form-group">
              <label className="form-group-label">Ação:</label>
              <select className="filter-select"
                value={percentageType}
                onChange={(e) => setPercentageType(e.target.value)}>
                <option disabled value="">Selecione uma opção</option>
                <option value="increase">Aumento</option>
                <option value="decrease">Redução</option>
              </select>
            </div>
            <div className="divisor-enterprise"></div>
            <div className="form-group">
              <label className="form-group-label">Porcentagem de aumento:</label>
              <input
                type="number"
                required
                placeholder="%"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
              />
            </div>
            <div className="divisor-enterprise"></div>
            <div className="form-buttons">
              <button type="button" className="cancel-button-unidade" onClick={handleCloseUnitIncreasedModal}>
                Cancelar
              </button>
              <button type="submit" className="confirm-button" onClick={handleUpdatePercentageModal}>
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão- unidades */}
      {isDeleteModalUnitOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Tem certeza que deseja excluir esta unidade?</h3>
            <p>Ao confirmar, essa ação não poderá ser revertida.</p>
            <div className="modal-actions">
              <button
                className="cancel-button-unidade"
                onClick={handleCancelDeleteUnits}
              >
                Cancelar
              </button>
              <button
                className="confirm-button"
                onClick={confirmDeleteUnits}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal para editar as informações gerais */}
      {isEditModalInfoOpen && (
        <div className="modal-overlay-planta">
          <div className="modal-planta-popup">
            <div className="modal-header-planta">
              <h2>Editar informações gerais:</h2>
              <button
                className="close-button-planta"
                onClick={handleCloseEditModal}
              >
                ✕
              </button>
            </div>
            <p>Edite os dados abaixo para atualizar as informações.</p>
            <div className="divisor-enterprise"></div>
            <form className="modal-form-planta">
              <div className="form-row-planta">
                <div>
                  <label>Nome:</label>
                  <input
                    type="text"
                    required
                    placeholder="Digite o nome"
                    value={updatedEnterprise.name}
                    onChange={(e) =>
                      setUpdatedEnterprise({ ...updatedEnterprise, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="releaseType">Tipo de lançamento:</label>
                  <select
                    id="releaseType"
                    value={updatedEnterprise.releaseType.id}
                    className="select-info"
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        releaseType: {
                          ...updatedEnterprise.releaseType,
                          id: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="" disabled>
                      Selecione o tipo de lançamento
                    </option>
                    {releaseTypeProvider
                      .getReleaseTypes()
                      .map((rt) => (
                        <option key={rt.id} value={rt.id}>
                          {rt.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label htmlFor="releaseType">Categoria:</label>
                  <select
                    id="releaseType"
                    value={updatedEnterprise.category.id}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        category: {
                          ...updatedEnterprise.category,
                          id: e.target.value,
                        },
                      })
                    }
                    className="select-info">
                    <option value="" disabled>
                      Selecione a categoria
                    </option>
                    {propertyCategoryProvider.getPropertyCategorys()
                      .filter(category =>
                        category.name === "Condomínio Vertical" ||
                        category.name === "Condomínio Horizontal"
                      )
                      .map((prop) => (
                        <option key={prop.id} value={prop.id}>
                          {prop.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="releaseType">Construtora:</label>
                  <select
                    id="releaseType"
                    value={updatedEnterprise.constructionCompany.id}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        constructionCompany: {
                          ...updatedEnterprise.constructionCompany,
                          id: e.target.value,
                        },
                      })
                    }
                    className="select-info">
                    <option value="" disabled>
                      Selecione a construtora
                    </option>
                    {constructionCompanyProvider.getConstructionCompanies().map((prop) => (
                      <option key={prop.id} value={prop.id}>
                        {prop.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Descrição:</label>
                  <input
                    placeholder="Digite a descrição"
                    name="description"
                    value={updatedEnterprise.description}
                    onChange={(e) =>
                      setUpdatedEnterprise({ ...updatedEnterprise, description: e.target.value })
                    }
                  ></input>
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>CEP:</label>
                  <input
                    type="text"
                    placeholder="Digite o CEP"
                    name="zipCode"
                    value={updatedEnterprise.address.zipCode.getCodeFormatted()}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        address: {
                          ...updatedEnterprise.address,
                          zipCode: new ZipCode(e.target.value)
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label>Cidade:</label>
                  <input
                    type="text"
                    placeholder="Digite a cidade"
                    name="city"
                    value={updatedEnterprise.address.city}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        address: {
                          ...updatedEnterprise.address,
                          city: e.target.value
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Logradouro:</label>
                  <input
                    type="text"
                    placeholder="Digite o logradouro"
                    name="street"
                    value={updatedEnterprise.address.street}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        address: {
                          ...updatedEnterprise.address,
                          street: e.target.value
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label>Número:</label>
                  <input
                    type="text"
                    placeholder="Digite o número"
                    name="number"
                    value={updatedEnterprise.address.number}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        address: {
                          ...updatedEnterprise.address,
                          number: e.target.value
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Bairro:</label>
                  <input
                    type="text"
                    placeholder="Digite o bairro"
                    name="neighborhood"
                    value={updatedEnterprise.address.neighborhood}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        address: {
                          ...updatedEnterprise.address,
                          neighborhood: e.target.value
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label>Estado:</label>
                  <input
                    type="text"
                    placeholder="Digite o estado"
                    name="state"
                    value={updatedEnterprise.address.uf}
                    onChange={(e) =>
                      setUpdatedEnterprise({
                        ...updatedEnterprise,
                        address: {
                          ...updatedEnterprise.address,
                          uf: e.target.value
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className="form-buttons-planta">
                <button
                  type="button"
                  className="cancel-button-unidade"
                  onClick={handleCloseEditModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="create-button-planta"
                  onClick={handleUpdateEnterprise}
                >
                  Salvar alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para adicionar e remover tags */}
      {modalOpen && (
        <div className="modal-overlay-enterprise">
          <div className="modal-enterprise">
            <div className="modal-header-enterprise">
              <h2>{modalContent.title}</h2>
              <button
                onClick={() => setModalOpen(false)}
                className="close-button-enterprise"
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-content-enterprises">
              {modalContent.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button
                    className="remove-tag"
                    onClick={() => handleRemoveTag(index)}
                  >
                    ✕
                  </button>
                </span>
              ))}
              <div className="add-tag-section">
                <input
                  type="text"
                  placeholder="Adicionar novas tags"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className="add-tag-input"
                />
                <button onClick={handleAddTag} className="add-tag-button">
                  + Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para adicionar novas plantas */}
      {isPlantModalOpen && (
        <div className="modal-overlay-planta">
          <div className="modal-planta-popup">
            <div className="modal-header-planta">
              <h2>Adicionar planta:</h2>
              <button
                className="close-button-planta"
                onClick={handleClosePlantModal}
              >
                ✕
              </button>
            </div>
            <p>Preencha os dados abaixo para criar uma nova planta.</p>
            <div className="divisor-enterprise"></div>
            <form className="modal-form-planta" onSubmit={handleCreatePlant}>
              <div className="form-row-planta">
                <div>
                  <label>Nome da planta:</label>
                  <input
                    type="text"
                    required
                    placeholder="Digite o nome da planta"
                    value={projectPlanForm.name}
                    onChange={(e) => setProjectPlanForm({ ...projectPlanForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label>Qtde de Quartos:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Digite um número"
                    value={projectPlanForm.rooms[0].quantity || 0}
                    onChange={(e) => handleInputChange(e, 0, "quantity")}
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Qtde de Banheiros:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Digite um número"
                    value={projectPlanForm.rooms[1].quantity || 0}
                    onChange={(e) =>
                      setProjectPlanForm({
                        ...projectPlanForm,
                        rooms: projectPlanForm.rooms.map((room, index) =>
                          index === 1
                            ? { ...room, quantity: parseInt(e.target.value) || 0 }
                            : room
                        ),
                      })
                    }
                  />
                </div>
                <div>
                  <label>Qtde de vagas:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Digite um número"
                    value={projectPlanForm.rooms[2].quantity || 0}
                    onChange={(e) =>
                      setProjectPlanForm({
                        ...projectPlanForm,
                        rooms: projectPlanForm.rooms.map((room, index) =>
                          index === 2
                            ? { ...room, quantity: parseInt(e.target.value) || 0 }
                            : room
                        ),
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Área total (m²):</label>
                  <input
                    type="number"
                    required
                    placeholder="Digite a área total"
                    value={projectPlanForm.area}
                    onChange={(e) =>
                      setProjectPlanForm({
                        ...projectPlanForm,
                        area: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <label>Área útil/privativa (m²):</label>
                  <input
                    type="number"
                    required
                    placeholder="Digite a area útil/privativa"
                    value={projectPlanForm.builtArea}
                    onChange={(e) =>
                      setProjectPlanForm({
                        ...projectPlanForm,
                        builtArea: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div>
                <h2 className="step-header data-box-details-h2">Upload de fotos:</h2>
                <div
                  className="upload-area"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDropProjectPlanImage}
                  onClick={() => document.getElementById("file-input")?.click()}
                >
                  <div className="upload-icon"></div>
                  <Upload size={24} />
                  <p className="upload-text">
                    Arraste e solte uma ou mais fotos aqui, ou clique para
                    selecionar diretamente do seu dispositivo.
                  </p>
                </div>

                <input
                  id="file-input"
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  onChange={(e) =>
                    e.target.files &&
                    handleProjectPlanFileChange(e.target.files)
                  }
                  style={{ display: "none" }}
                />

                <div className="upload-info">
                  <p>Formatos suportados: PNG e JPEG</p>
                  <p>Tamanho máximo da foto: 10 Mb</p>
                </div>
                <div className="divisor"></div>
                {projectPlanImage.file && (
                  <div className="image-preview-container">
                    <div className="image-preview">
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteProjectPlanImage()}
                      >
                        &times;
                      </button>
                      <div className="details">
                        <div className="image-thumbnail">
                          <img
                            src={URL.createObjectURL(projectPlanImage.file)}
                            alt={`Preview ${projectPlanImage.file.name}`}
                            className="preview-image"
                          />
                        </div>
                        <div>
                          <p className="file-name">
                            {projectPlanImage.file.name}
                          </p>
                          <p className="file-size">
                            {(projectPlanImage.file.size / 1024 / 1024).toFixed(
                              2
                            )}{" "}
                            Mb
                          </p>
                        </div>
                      </div>

                      <div className="progress-container">
                        <div className="progress-bar-img">
                          <div
                            className="progress-img"
                            style={{ width: `${projectPlanImage.progress}%` }}
                          ></div>
                        </div>
                        <span className="progress-percentage">
                          {projectPlanImage.progress}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-buttons-planta">
                <button
                  type="button"
                  className="cancel-button-unidade"
                  onClick={handleClosePlantModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`create-button-planta ${isLoading ? "loading" : ""}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span >Criando planta...</span>
                  ) : (
                    "Criar planta"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para editar plantas */}
      {isEditModalOpen && selectedPlan && (
        <div className="modal-overlay-planta">
          <div className="modal-planta-popup">
            <div className="modal-header-planta">
              <h2 className="data-box-details-title-h2">Editar planta:</h2>
              <button
                className="close-button-planta"
                onClick={() => setIsEditModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <p>Edite os dados abaixo para atualizar a planta.</p>
            <div className="divisor-enterprise"></div>
            <form className="modal-form-planta" onSubmit={handleSavePlantChanges}>
              <div className="form-row-planta">
                <div>
                  <label>Nome da planta:</label>
                  <input
                    type="text"
                    required
                    placeholder="Digite o nome da planta"
                    value={selectedPlan.name}
                    onChange={(e) =>
                      setSelectedPlan({
                        ...selectedPlan,
                        name: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label>Qtde de Quartos:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Digite um número"
                    value={selectedPlan.rooms[0].quantity || 0}
                    onChange={(e) =>
                      setSelectedPlan({
                        ...selectedPlan,
                        rooms: selectedPlan.rooms.map((room, index) =>
                          index === 0
                            ? { ...room, quantity: parseInt(e.target.value) || 0 }
                            : room
                        ),
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Qtde de Banheiros:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Digite um número"
                    value={selectedPlan.rooms[1].quantity || 0}
                    onChange={(e) =>
                      setSelectedPlan({
                        ...selectedPlan,
                        rooms: selectedPlan.rooms.map((room, index) =>
                          index === 1
                            ? { ...room, quantity: parseInt(e.target.value) || 0 }
                            : room
                        ),
                      })
                    }
                  />
                </div>
                <div>
                  <label>Qtde de vagas:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Digite um número"
                    value={selectedPlan.rooms[2].quantity || 0}
                    onChange={(e) =>
                      setSelectedPlan({
                        ...selectedPlan,
                        rooms: selectedPlan.rooms.map((room, index) =>
                          index === 2
                            ? { ...room, quantity: parseInt(e.target.value) || 0 }
                            : room
                        ),
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-planta">
                <div>
                  <label>Área total (m²):</label>
                  <input
                    type="number"
                    required
                    placeholder="Digite a área total"
                    value={selectedPlan.area}
                    onChange={(e) =>
                      setSelectedPlan({
                        ...selectedPlan,
                        area: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <label>Área útil/privativa (m²):</label>
                  <input
                    type="number"
                    required
                    placeholder="Digite a área útil/privativa"
                    value={selectedPlan.builtArea}
                    onChange={(e) =>
                      setSelectedPlan({
                        ...selectedPlan,
                        builtArea: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>

              {/* Upload de fotos */}
              <h2 className="step-header data-box-details-title-h2">Fotos:</h2>

              {/* Botão para adicionar novas imagens */}
              <div className="upload-section">
                <input
                  id="file-input-plan"
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  onChange={(e) =>
                    e.target.files &&
                    handleAddImagesToPlan(e.target.files)
                  }
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  className="upload-button"
                  onClick={() => document.getElementById('file-input-plan')?.click()}
                >
                  <Upload size={20} />
                  Adicionar Imagens
                </button>
              </div>

              {/* Fotos atuais */}
              <div className="divisor-enterprise"></div>
              {selectedPlan.images && selectedPlan.images.length > 0 && (
                <div className="image-preview-container">
                  <h3>Imagens atuais:</h3>
                  {selectedPlan.images.map((image: any, index: number) => (
                    <div key={index} className="image-preview">
                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => handleRemoveImageFromPlan(image.id)}
                        title="Remover imagem"
                      >
                        &times;
                      </button>
                      <div className="details">
                        <div className="image-thumbnail">
                          <img
                            src={image.file ? URL.createObjectURL(image.file) : image.imageLink}
                            alt={`Preview ${index + 1}`}
                            className="preview-image"
                            onError={(e) => {
                              e.currentTarget.src = 'https://via.placeholder.com/150?text=Imagem+não+encontrada';
                            }}
                          />
                        </div>
                        {image.file && (
                          <div>
                            <p className="file-name">
                              {image.file.name}
                            </p>
                            <p className="file-size">
                              {(image.file.size / 1024 / 1024).toFixed(2)} Mb
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(!selectedPlan.images || selectedPlan.images.length === 0) && (
                <div className="no-images">
                  <p>Nenhuma imagem adicionada ainda.</p>
                  <p>Use o botão acima para adicionar imagens à planta.</p>
                </div>
              )}

              <div className="form-buttons-planta">
                <button
                  type="button"
                  className="cancel-button-unidade"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`create-button-planta ${isLoading ? "loading" : ""}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span>Salvando planta...</span>
                  ) : (
                    "Salvar alterações"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para adicionar novas unidades */}
      {isUnitModalOpen && (
        <div className="modal-overlay-unidade">
          <div className="modal-unidade">
            <div className="modal-header-unidade">
              <h2>Adicionar unidade:</h2>
              <button
                className="close-button-unidade"
                onClick={handleCloseUnitModal}
              >
                ✕
              </button>
            </div>
            <p>Preencha os dados abaixo para criar uma nova unidade.</p>
            <div className="divisor-enterprise"></div>
            <form className="modal-form-unidade" onSubmit={handleCreateUnit}>
              <div className="form-row-unidade">
                <div>
                  <label>Nome da unidade:</label>
                  <input
                    type="text"
                    required
                    placeholder="Digite o nome da unidade"
                    value={unitForm.description}
                    onChange={(e) =>
                      setUnitForm({ ...unitForm, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label>Preço:</label>
                  <input
                    type="text"
                    required
                    placeholder="R$"
                    value={unitForm.price ? Formatter.formatNumberToBRCurrency(unitForm.price) : ""}
                    onChange={(e) => {
                      const value = Formatter.clearSymbolsAndLetters(
                        e.target.value
                      );
                      setUnitForm({
                        ...unitForm,
                        price: Number(value),
                      });
                    }}
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-unidade">
                <div>
                  <label>Tipo de unidade:</label>
                  <select
                    required
                    value={unitForm.tipoUnidade}
                    onChange={(e) =>
                      setUnitForm({ ...unitForm, tipoUnidade: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Selecione o tipo de unidade
                    </option>
                    <option value="apartment">Apartamento</option>
                    <option value="house">Casa</option>
                  </select>
                </div>
                <div>
                  <label>Planta:</label>
                  <select
                    id="planta"
                    required
                    value={unitForm.projectPlanId}
                    onChange={(e) =>
                      setUnitForm({
                        ...unitForm,
                        projectPlanId: e.target.value,
                      })
                    }
                  >
                    <option value="" disabled>
                      Selecione a planta:
                    </option>
                    {enterprise.projectPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              {unitForm.tipoUnidade === "apartment" && (
                <div className="form-row-unidade">
                  <div>
                    <label>Complemento:</label>
                    <input
                      type="text"
                      required
                      placeholder="Digite o bloco, quadra e/ou torre"
                      value={unitForm.complemento}
                      onChange={(e) =>
                        setUnitForm({ ...unitForm, complemento: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
              {unitForm.tipoUnidade === "house" && (
                <>
                  <div className="form-row-unidade">
                    <div>
                      <label>Complemento:</label>
                      <input
                        type="text"
                        required
                        placeholder="Digite o número ou lote"
                        value={unitForm.complemento}
                        onChange={(e) =>
                          setUnitForm({ ...unitForm, complemento: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="divisor-enterprise"></div>
                  <div className="form-row-unidade">
                    <div>
                      <label>Rua:</label>
                      <input
                        type="text"
                        required
                        placeholder="Digite a rua"
                        value={unitForm.endereco.rua}
                        onChange={(e) =>
                          setUnitForm({
                            ...unitForm,
                            endereco: { ...unitForm.endereco, rua: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div>
                      <label>Número:</label>
                      <input
                        type="text"
                        required
                        placeholder="Digite o número"
                        value={unitForm.endereco.numero}
                        onChange={(e) =>
                          setUnitForm({
                            ...unitForm,
                            endereco: { ...unitForm.endereco, numero: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="divisor-enterprise"></div>
              <div>
                <label>Status:</label>
                <select
                  id="status"
                  required
                  value={unitForm.status.getStatus()}
                  onChange={(e) => {
                    setUnitForm({
                      ...unitForm,
                      status: new UnitStatus(e.target.value),
                    });
                  }}
                >
                  <option value="" disabled>
                    Selecione o status:
                  </option>
                  {unitStatus.ALL.map((plan) => (
                    <option
                      key={plan}
                      value={plan}
                    >
                      {unitStatus.unitStatusMap[plan]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-buttons-unidade">
                <button
                  type="button"
                  className="cancel-button-unidade"
                  onClick={handleCloseUnitModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`create-button-unidade ${isLoading ? "loading" : ""}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span>Salvando unidade...</span>
                  ) : (
                    "Salvar alterações"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para editar unidades */}
      {isEditUnitModalOpen && (
        <div className="modal-overlay-unidade">
          <div className="modal-unidade">
            <div className="modal-header-unidade">
              <h2>Editar unidade:</h2>
              <button
                className="close-button-unidade"
                onClick={handleCloseEditUnitModal}
              >
                ✕
              </button>
            </div>
            <p>Edite os dados abaixo para atualizar a unidade.</p>
            <div className="divisor-enterprise"></div>
            <form className="modal-form-unidade" onSubmit={handleSaveUnitChanges}>
              <div className="form-row-unidade">
                <div>
                  <label>Nome da unidade:</label>
                  <input
                    type="text"
                    required
                    placeholder="Digite o nome da unidade"
                    value={unitForm.description}
                    onChange={(e) =>
                      setUnitForm({ ...unitForm, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label>Preço:</label>
                  <input
                    type="text"
                    required
                    placeholder="R$"
                    value={unitForm.price ? Formatter.formatNumberToBRCurrency(unitForm.price) : ""}
                    onChange={(e) => {
                      const value = Formatter.clearSymbolsAndLetters(
                        e.target.value
                      );
                      setUnitForm({
                        ...unitForm,
                        price: Number(value),
                      });
                    }}
                  />
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              <div className="form-row-unidade">
                <div>
                  <label>Tipo de unidade:</label>
                  <select
                    required
                    value={unitForm.tipoUnidade}
                    onChange={(e) =>
                      setUnitForm({ ...unitForm, tipoUnidade: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Selecione o tipo de unidade
                    </option>
                    <option value="apartment">Apartamento</option>
                    <option value="house">Casa</option>
                  </select>
                </div>
                <div>
                  <label>Planta:</label>
                  <select
                    id="planta"
                    required
                    value={unitForm.projectPlanId}
                    onChange={(e) =>
                      setUnitForm({
                        ...unitForm,
                        projectPlanId: e.target.value,
                      })
                    }
                  >
                    <option value="" disabled>
                      Selecione a planta:
                    </option>
                    {enterprise.projectPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="divisor-enterprise"></div>
              {unitForm.tipoUnidade === "apartment" && (
                <div className="form-row-unidade">
                  <div>
                    <label>Complemento:</label>
                    <input
                      type="text"
                      required
                      placeholder="Digite o bloco, quadra e/ou torre"
                      value={unitForm.complemento}
                      onChange={(e) =>
                        setUnitForm({ ...unitForm, complemento: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
              {unitForm.tipoUnidade === "house" && (
                <>
                  <div className="form-row-unidade">
                    <div>
                      <label>Complemento:</label>
                      <input
                        type="text"
                        required
                        placeholder="Digite o número ou lote"
                        value={unitForm.complemento}
                        onChange={(e) =>
                          setUnitForm({ ...unitForm, complemento: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="divisor-enterprise"></div>
                  <div className="form-row-unidade">
                    <div>
                      <label>Rua:</label>
                      <input
                        type="text"
                        required
                        placeholder="Digite a rua"
                        value={unitForm.endereco.rua}
                        onChange={(e) =>
                          setUnitForm({
                            ...unitForm,
                            endereco: { ...unitForm.endereco, rua: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div>
                      <label>Número:</label>
                      <input
                        type="text"
                        required
                        placeholder="Digite o número"
                        value={unitForm.endereco.numero}
                        onChange={(e) =>
                          setUnitForm({
                            ...unitForm,
                            endereco: { ...unitForm.endereco, numero: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="divisor-enterprise"></div>
              <div>
                <label>Status:</label>
                <select
                  id="status"
                  required
                  value={unitForm.status.getStatus()}
                  onChange={(e) => {
                    setUnitForm({
                      ...unitForm,
                      status: new UnitStatus(e.target.value),
                    });
                  }}
                >
                  <option value="" disabled>
                    Selecione o status:
                  </option>
                  {unitStatus.ALL.map((plan) => (
                    <option
                      key={plan}
                      value={plan}
                    >
                      {unitStatus.unitStatusMap[plan]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-buttons-unidade">
                <button
                  type="button"
                  className="cancel-button-unidade"
                  onClick={handleCloseEditUnitModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`create-button-unidade ${isLoading ? "loading" : ""}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span>Salvando unidade...</span>
                  ) : (
                    "Salvar alterações"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default EnterprisesDetails;
